// src/components/Footer.tsx
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="relative mt-20 bg-slate-950">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and Brand */}
          <div className="footer-card-glow p-8">
            <Link 
              to="/" 
              onClick={scrollToTop}
              className="text-3xl font-lora font-semibold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent hover:scale-105 transition-transform duration-300 inline-block"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: 0,
                marginBottom: 0
              }}
            >
              Allmaverse
            </Link>
            <p className="mt-4 text-white/70 text-base leading-relaxed">
              Unikatna umetnost, oblikovanje in ustvarjanje iz srca Maribora.
            </p>
            <div className="mt-6 space-y-3">
              <a 
                href="https://instagram.com/allmaverse" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-white/70 hover:text-purple-300 transition-colors group"
              >
                <Instagram size={22} className="group-hover:scale-110 transition-transform" />
                <span className="text-base">@allmaverse</span>
              </a>
              <a 
                href="https://facebook.com/allmaverse" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-white/70 hover:text-purple-300 transition-colors group"
              >
                <Facebook size={22} className="group-hover:scale-110 transition-transform" />
                <span className="text-base">Allmaverse</span>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="footer-card-glow p-8">
            <h3 className="text-xl font-semibold text-white mb-6">Navigacija</h3>
            <ul className="space-y-3">
              {[
                { href: '/', label: 'Domov' },
                { href: '/o-nas', label: 'O nas' },
                { href: '/storitev', label: 'Storitve' },
                { href: '/galerija', label: 'Galerija' },
                { href: '/kontakt', label: 'Kontakt' }
              ].map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-white/70 hover:text-purple-300 transition-all duration-300 text-base group flex items-center"
                  >
                    <span className="group-hover:translate-x-2 transition-transform duration-300">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="footer-card-glow p-8">
            <h3 className="text-xl font-semibold text-white mb-6">Pravno</h3>
            <ul className="space-y-3">
              {[
                { href: '/pravni-dokumenti/politika-zasebnosti', label: 'Politika zasebnosti' },
                { href: '/pravni-dokumenti/pogoji-uporabe', label: 'Pogoji uporabe' },
                { href: '/pravni-dokumenti/politika-piskotkov', label: 'Politika piškotkov' }
              ].map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    onClick={scrollToTop}
                    className="text-white/70 hover:text-purple-300 transition-all duration-300 text-base group flex items-center"
                  >
                    <span className="group-hover:translate-x-2 transition-transform duration-300">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="footer-card-glow p-8">
            <h3 className="text-xl font-semibold text-white mb-6">Kontakt</h3>
            <ul className="space-y-4">
              <li className="flex items-center space-x-3 group">
                <Mail size={18} className="text-purple-400 group-hover:text-purple-300 transition-colors" />
                <a 
                  href="mailto:alma.lovrencic@gmail.com"
                  className="text-white/70 hover:text-purple-300 transition-colors text-base group-hover:translate-x-1 transition-transform duration-300"
                >
                  alma.lovrencic@gmail.com
                </a>
              </li>
              <li className="flex items-center space-x-3 group">
                <Phone size={18} className="text-purple-400 group-hover:text-purple-300 transition-colors" />
                <a 
                  href="tel:+38651479667"
                  className="text-white/70 hover:text-purple-300 transition-colors text-base group-hover:translate-x-1 transition-transform duration-300"
                >
                  +386 51 479 667
                </a>
              </li>
              <li className="flex items-start space-x-3 group">
                <MapPin size={18} className="text-purple-400 mt-0.5 group-hover:text-purple-300 transition-colors" />
                <span className="text-white/70 text-base group-hover:text-white transition-colors">
                  Žički prehod 4<br />
                  2000 Maribor, Slovenia
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 text-center">
          <p className="text-white/60 text-base">
            © 2025 Allmaverse. Vse pravice pridržane. Izdelano z ❤️ v Mariboru. 
            <a 
              href="https://digitalpro.dev/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-300 hover:text-purple-200 transition-colors ml-1 underline decoration-purple-300 hover:decoration-purple-200"
            >
              DigitalPro
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;